import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DataTable } from '../../../shared/components/DataTable';
import { AuthContext } from '../../../shared/auth/AuthContext';
import { ErrorState } from '../../../shared/components/ErrorState';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { MonthFilterField, fromMonthValue, toMonthValue } from '../../../shared/components/MonthFilterField';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useCompanyNotasFiscaisHooks } from './hooks';
import { useCompanyNotasFiscaisStream } from './useCompanyNotasFiscaisStream';
import { CompanyNotaFiscalCancelarDialog } from './CompanyNotaFiscalCancelarDialog';
import { CompanyNotaFiscalUploadDialog } from './CompanyNotaFiscalUploadDialog';
import { NotaFiscalEventosDialog } from './NotaFiscalEventosDialog';
import { RelatorioExtratoDialog } from './RelatorioExtratoDialog';
import { CompanyNotaFiscalAcoesMenu } from './CompanyNotaFiscalAcoesMenu';
import { CompanyNotaFiscalMostrarDialog } from './CompanyNotaFiscalMostrarDialog';
import {
  defaultNfEmpresaGridSnapshot,
  loadNfEmpresaGridState,
  NF_EMPRESA_PAGE_SIZE,
  saveNfEmpresaGridState,
  type NfEmpresaGridSnapshot,
} from './nfEmpresaGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import type {
  CompanyNotaFiscalListItem,
  NotaFiscalEmpresaStatus,
  RelatorioExtratoParams,
} from './types';

const STATUS_FILTER_OPTIONS: NotaFiscalEmpresaStatus[] = ['PEM', 'EMI', 'CAN', 'PCA'];

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function statusChipColor(
  status: NotaFiscalEmpresaStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'PEM':
      return 'warning';
    case 'EMI':
      return 'success';
    case 'PCA':
      return 'info';
    case 'CAN':
      return 'default';
  }
}

/**
 * Cor do chip do `statusEnvio` — eixo ortogonal ao `status`. Domínio observado
 * na KB: PEN (aguardando ServiceNotaFiscal.exe), ENV (enviado, aguardando
 * SEFAZ), AUT (autorizado), EER (erro transitório no envio), ERR (erro
 * persistente — removida da fila até correção; ex.: certificado vencido),
 * CAN (cancelamento processado). Ausente/vazio = sem integração ainda.
 */
function statusEnvioChipColor(
  statusEnvio: string,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (statusEnvio) {
    case 'AUT':
      return 'success';
    case 'EER':
    case 'ERR':
      return 'error';
    case 'PEN':
      return 'warning';
    case 'ENV':
      return 'info';
    case 'CAN':
      return 'default';
    default:
      return 'default';
  }
}

/** Marca NFs com erro persistente/transitório na integração fiscal. */
function isErroEnvio(statusEnvio: string): boolean {
  return statusEnvio === 'ERR' || statusEnvio === 'EER';
}

export interface CompanyNotasFiscaisManagerProps {
  companyId: number | null;
  /** Zera a emitente da página. "Limpar filtros" também volta o tamanho para 20. */
  onClearEmpresa?: () => void;
}

export function CompanyNotasFiscaisManager({
  companyId,
  onClearEmpresa,
}: CompanyNotasFiscaisManagerProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const userId = auth?.status === 'bootstrapping' ? undefined : (auth?.user?.externalId ?? null);
  const [snapshot, setSnapshot] = useState<NfEmpresaGridSnapshot | null>(null);

  useEffect(() => {
    if (userId === undefined) return;
    setSnapshot(userId == null ? defaultNfEmpresaGridSnapshot() : (loadNfEmpresaGridState(userId) ?? defaultNfEmpresaGridSnapshot()));
  }, [userId]);

  useEffect(() => {
    if (!snapshot || userId == null) return;
    saveNfEmpresaGridState(userId, { ...snapshot, empresaId: companyId });
  }, [snapshot, companyId, userId]);

  function patch(partial: Partial<NfEmpresaGridSnapshot>): void {
    setSnapshot((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  function patchFilters(partial: Partial<Omit<NfEmpresaGridSnapshot, 'query' | 'empresaId'>>): void {
    setSnapshot((prev) =>
      prev ? { ...prev, ...partial, query: { ...prev.query, page: 1 } } : prev,
    );
  }

  const statusFiltro = snapshot?.status ?? '';
  const anoFiltro = snapshot?.periodoAno ?? '';
  const mesFiltro = snapshot?.periodoMes ?? '';
  const emissaoAnoFiltro = snapshot?.emissaoAno ?? '';
  const emissaoMesFiltro = snapshot?.emissaoMes ?? '';
  const clienteFiltro = snapshot?.clienteCodigo ?? null;
  const rcFiltro = snapshot?.requisicaoCompraReferencia ?? '';
  const colaboradorFiltro = snapshot?.colaboradorCodigo ?? null;
  const query = snapshot?.query ?? defaultNfEmpresaGridSnapshot().query;

  function setStatusFiltro(status: NotaFiscalEmpresaStatus | ''): void {
    patchFilters({ status });
  }
  function setCompetencia(value: string): void {
    const parsed = fromMonthValue(value);
    patchFilters({
      periodoAno: parsed.year ?? '',
      periodoMes: parsed.month ?? '',
    });
  }
  function setEmissao(value: string): void {
    const parsed = fromMonthValue(value);
    patchFilters({
      emissaoAno: parsed.year ?? '',
      emissaoMes: parsed.month ?? '',
    });
  }
  function setClienteFiltro(clienteCodigo: number | null): void {
    patchFilters({ clienteCodigo });
  }
  function setRcFiltro(requisicaoCompraReferencia: string): void {
    patchFilters({ requisicaoCompraReferencia });
  }
  function setColaboradorFiltro(colaboradorCodigo: number | null): void {
    patchFilters({ colaboradorCodigo });
  }
  function setQuery(next: NfEmpresaGridSnapshot['query']): void {
    patch({ query: next });
  }

  function limparFiltros(): void {
    setSnapshot(defaultNfEmpresaGridSnapshot());
    onClearEmpresa?.();
  }
  const [mostrarFor, setMostrarFor] = useState<CompanyNotaFiscalListItem | null>(null);
  const [cancelarFor, setCancelarFor] = useState<CompanyNotaFiscalListItem | null>(null);
  const [uploadFor, setUploadFor] = useState<CompanyNotaFiscalListItem | null>(null);
  const [eventosFor, setEventosFor] = useState<CompanyNotaFiscalListItem | null>(null);
  const [reenviarFor, setReenviarFor] = useState<CompanyNotaFiscalListItem | null>(null);
  const [relatorioOpen, setRelatorioOpen] = useState(false);

  const listExtra = {
    status: statusFiltro || undefined,
    periodoAno: anoFiltro === '' || mesFiltro === '' ? undefined : anoFiltro,
    periodoMes: anoFiltro === '' || mesFiltro === '' ? undefined : mesFiltro,
    emissaoAno: emissaoAnoFiltro === '' || emissaoMesFiltro === '' ? undefined : emissaoAnoFiltro,
    emissaoMes: emissaoAnoFiltro === '' || emissaoMesFiltro === '' ? undefined : emissaoMesFiltro,
    clienteCodigo: clienteFiltro ?? undefined,
    requisicaoCompraReferencia: rcFiltro.trim() || undefined,
    colaboradorCodigo: colaboradorFiltro ?? undefined,
  };
  const hooks = useCompanyNotasFiscaisHooks(companyId, listExtra, snapshot !== null);
  const listQuery = hooks.useList(query);
  // Push de mudança de status via SSE (auto-refresh do grid). Complementa o
  // botão Atualizar manual (que fica como fallback / refresh imediato).
  const streamStatus = useCompanyNotasFiscaisStream(
    companyId,
    query,
    listExtra,
    snapshot !== null,
  );
  const reenviarMut = hooks.useReenviar();
  const relatorioMut = hooks.useRelatorioExtrato();

  /** Monta os params do relatório a partir dos filtros ativos do grid. */
  function relatorioParams(imprimeEspelho: boolean): RelatorioExtratoParams {
    return {
      imprimeEspelho,
      periodoAno: anoFiltro === '' || mesFiltro === '' ? undefined : anoFiltro,
      periodoMes: anoFiltro === '' || mesFiltro === '' ? undefined : mesFiltro,
      clienteCodigo: clienteFiltro ?? undefined,
      requisicaoCompraReferencia: rcFiltro.trim() || undefined,
      colaboradorCodigo: colaboradorFiltro ?? undefined,
    };
  }

  async function gerarRelatorio(imprimeEspelho: boolean): Promise<void> {
    const params = relatorioParams(imprimeEspelho);
    try {
      const blob = await relatorioMut.mutateAsync(params);
      const periodo =
        params.periodoAno && params.periodoMes
          ? `${params.periodoAno}-${String(params.periodoMes).padStart(2, '0')}`
          : 'sem-periodo';
      const filename = `extrato-nf-${periodo}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setRelatorioOpen(false);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<CompanyNotaFiscalListItem>[] = [
    {
      field: 'acoes',
      headerName: t('features.empresa.notasFiscais.actions.menu'),
      width: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <CompanyNotaFiscalAcoesMenu
          row={params.row}
          onMostrar={() => setMostrarFor(params.row)}
          onUpload={() => setUploadFor(params.row)}
          onCancelar={() => setCancelarFor(params.row)}
          onEventos={() => setEventosFor(params.row)}
          onReenviar={() => setReenviarFor(params.row)}
        />
      ),
    },
    {
      field: 'status',
      headerName: t('features.empresa.notasFiscais.columns.status'),
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const s = params.row.status;
        const chip = (
          <Chip
            size="small"
            label={t(`features.empresa.notasFiscais.status.${s}`)}
            color={statusChipColor(s)}
            variant={s === 'EMI' ? 'filled' : 'outlined'}
          />
        );
        const motivo = params.row.motivoCancelamento;
        if ((s === 'CAN' || s === 'PCA') && motivo) {
          return (
            <Tooltip title={t('features.empresa.notasFiscais.status.canceladaTooltip', { motivo })}>
              {chip}
            </Tooltip>
          );
        }
        return chip;
      },
    },
    {
      field: 'empresaNome',
      headerName: t('features.empresa.notasFiscais.columns.empresa'),
      width: 180,
      sortable: false,
      valueGetter: (_, row) => row.empresaNome || '—',
    },
    {
      field: 'numero',
      headerName: t('features.empresa.notasFiscais.columns.numero'),
      width: 100,
      valueGetter: (_, row) => row.numero ?? `#${row.id}`,
    },
    {
      field: 'numeroRPS',
      headerName: t('features.empresa.notasFiscais.columns.rps'),
      width: 90,
      sortable: false,
      valueGetter: (_, row) => row.numeroRPS ?? '—',
    },
    {
      field: 'data',
      headerName: t('features.empresa.notasFiscais.columns.emitidaEm'),
      width: 120,
      valueGetter: (_, row) => row.data,
      valueFormatter: (value: string) => {
        const d = dayjs(value);
        return d.isValid() ? d.format('DD/MM/YYYY') : '—';
      },
    },
    {
      field: 'periodoLabel',
      headerName: t('features.empresa.notasFiscais.columns.periodo'),
      width: 140,
      sortable: false,
      valueGetter: (_, row) => row.periodoLabel ?? '—',
    },
    {
      field: 'clienteNome',
      headerName: t('features.empresa.notasFiscais.columns.cliente'),
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => row.clienteNome ?? '—',
    },
    {
      field: 'requisicaoCompraReferencia',
      headerName: t('features.empresa.notasFiscais.columns.rc'),
      width: 170,
      sortable: false,
      valueGetter: (_, row) => row.requisicaoCompraReferencia ?? '—',
    },
    {
      field: 'colaboradorNome',
      headerName: t('features.empresa.notasFiscais.columns.colaborador'),
      width: 160,
      sortable: false,
      valueGetter: (_, row) => row.colaboradorNome ?? '—',
    },
    {
      field: 'valor',
      headerName: t('features.empresa.notasFiscais.columns.valor'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number) =>
        typeof value === 'number' && Number.isFinite(value) ? formatBRL(value) : '—',
    },
    {
      field: 'valorImposto',
      headerName: t('features.empresa.notasFiscais.columns.imposto'),
      width: 120,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: number) =>
        typeof value === 'number' && Number.isFinite(value) ? formatBRL(value) : '—',
    },
    {
      field: 'valorLiquido',
      headerName: t('features.empresa.notasFiscais.columns.valorLiquido'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: number) =>
        typeof value === 'number' && Number.isFinite(value) ? formatBRL(value) : '—',
    },
    {
      field: 'statusEnvio',
      headerName: t('features.empresa.notasFiscais.columns.statusEnvio'),
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const se = params.row.statusEnvio || '';
        if (!se) {
          return (
            <Chip
              size="small"
              variant="outlined"
              color="default"
              label={t('features.empresa.notasFiscais.statusEnvio.empty')}
            />
          );
        }
        const known = ['PEN', 'ENV', 'AUT', 'EER', 'ERR', 'CAN'].includes(se);
        return (
          <Chip
            size="small"
            variant={se === 'AUT' ? 'filled' : 'outlined'}
            color={statusEnvioChipColor(se)}
            label={known ? t(`features.empresa.notasFiscais.statusEnvio.${se}`) : se}
          />
        );
      },
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  const rows = listQuery.data?.data ?? [];
  const totalRegistros = listQuery.data?.meta.total ?? 0;
  const soma = (campo: 'valor' | 'valorImposto' | 'valorLiquido'): number =>
    rows.reduce((acc, row) => acc + (Number(row[campo]) || 0), 0);

  return (
    <Stack spacing={2}>
      {/* Filtros à esquerda, ações à direita. Competência e emissão são um mês fechado cada. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            alignItems: 'start',
          }}
        >
          <TextField
            select
            size="small"
            fullWidth
            label={t('features.empresa.notasFiscais.filters.status')}
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value as NotaFiscalEmpresaStatus | '')}
          >
            <MenuItem value="">{t('features.empresa.notasFiscais.filters.statusTodos')}</MenuItem>
            {STATUS_FILTER_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {t(`features.empresa.notasFiscais.status.${s}`)}
              </MenuItem>
            ))}
          </TextField>
          <MonthFilterField
            fullWidth
            label={t('components.monthFilter.competencia')}
            value={toMonthValue(anoFiltro, mesFiltro)}
            onChange={setCompetencia}
          />
          <MonthFilterField
            fullWidth
            label={t('components.monthFilter.emissao')}
            value={toMonthValue(emissaoAnoFiltro, emissaoMesFiltro)}
            onChange={setEmissao}
          />
          <TextField
            size="small"
            fullWidth
            label={t('features.empresa.notasFiscais.filters.rc')}
            value={rcFiltro}
            onChange={(e) => setRcFiltro(e.target.value)}
          />
          <Box sx={{ gridColumn: 'span 2' }}>
            <ColaboradorSelect
              value={colaboradorFiltro}
              onChange={(id) => setColaboradorFiltro(id)}
              label={t('features.empresa.notasFiscais.filters.colaborador')}
              placeholder={t('features.empresa.notasFiscais.filters.colaboradorTodos')}
            />
          </Box>
          <Box sx={{ gridColumn: 'span 2' }}>
            <ClienteSelect
              value={clienteFiltro}
              onChange={(id) => setClienteFiltro(id)}
              label={t('features.empresa.notasFiscais.filters.cliente')}
              placeholder={t('features.empresa.notasFiscais.filters.clienteTodos')}
            />
          </Box>
        </Box>
          <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={limparFiltros}>
            {t('features.empresa.notasFiscais.filters.limpar')}
          </Button>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexShrink: 0 }}
          alignItems="center"
          flexWrap="wrap"
        >
          <Tooltip
            title={t(
              streamStatus === 'live'
                ? 'features.empresa.notasFiscais.stream.liveTooltip'
                : 'features.empresa.notasFiscais.stream.pollingTooltip',
            )}
          >
            <Chip
              size="small"
              variant="outlined"
              color={streamStatus === 'live' ? 'success' : 'info'}
              label={t(
                streamStatus === 'live'
                  ? 'features.empresa.notasFiscais.stream.live'
                  : 'features.empresa.notasFiscais.stream.polling',
              )}
            />
          </Tooltip>
          <Tooltip title={t('features.empresa.notasFiscais.actions.refreshTooltip')}>
            <span>
              <IconButton
                onClick={() => void listQuery.refetch()}
                disabled={listQuery.isFetching}
                aria-label={t('features.empresa.notasFiscais.actions.refresh')}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              companyId === null ? t('features.empresa.notasFiscaisPage.extratoPrecisaEmpresa') : ''
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                disabled={companyId === null}
                onClick={() => setRelatorioOpen(true)}
              >
                {t('features.empresa.notasFiscais.actions.imprimirExcel')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              companyId === null ? t('features.empresa.notasFiscaisPage.gerarPrecisaEmpresa') : ''
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={companyId === null}
                onClick={() => navigate(`/empresas/notas-fiscais/gerar?empresaId=${companyId}`)}
              >
                {t('features.empresa.notasFiscais.actions.new')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <DataTable<CompanyNotaFiscalListItem>
        rows={rows}
        columns={columns}
        total={totalRegistros}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        pageSizeOptions={[10, NF_EMPRESA_PAGE_SIZE, 50, 100]}
        searchPlaceholder={t('features.empresa.notasFiscais.searchPlaceholder')}
        gridProps={{
          getRowClassName: (params) =>
            isErroEnvio(params.row.statusEnvio || '') ? 'nf-linha-erro-envio' : '',
          sx: {
            minHeight: 360,
            '& .nf-linha-erro-envio': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)',
              '&:hover': {
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(244,67,54,0.20)' : 'rgba(244,67,54,0.14)',
              },
            },
          },
        }}
      />

      {rows.length > 0 && (
        <Stack direction="row" spacing={3} justifyContent="flex-end" flexWrap="wrap" sx={{ px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              totalRegistros > rows.length
                ? 'features.empresa.notasFiscais.totais.pagina'
                : 'features.empresa.notasFiscais.totais.filtro',
            )}
          </Typography>
          <Typography variant="body2">
            {t('features.empresa.notasFiscais.totais.quantidade', { count: rows.length })}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {t('features.empresa.notasFiscais.totais.valor', { valor: formatBRL(soma('valor')) })}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {t('features.empresa.notasFiscais.totais.imposto', {
              valor: formatBRL(soma('valorImposto')),
            })}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {t('features.empresa.notasFiscais.totais.liquido', {
              valor: formatBRL(soma('valorLiquido')),
            })}
          </Typography>
        </Stack>
      )}

      <CompanyNotaFiscalMostrarDialog
        open={mostrarFor !== null}
        nota={mostrarFor}
        onClose={() => setMostrarFor(null)}
      />

      <CompanyNotaFiscalCancelarDialog
        open={cancelarFor !== null}
        nota={cancelarFor}
        useCancelar={hooks.useCancelar}
        onClose={() => setCancelarFor(null)}
      />

      <CompanyNotaFiscalUploadDialog
        open={uploadFor !== null}
        nota={uploadFor}
        useUploadFile={hooks.useUploadFile}
        onClose={() => setUploadFor(null)}
      />

      <NotaFiscalEventosDialog
        open={eventosFor !== null}
        nota={eventosFor}
        hooks={hooks}
        onClose={() => setEventosFor(null)}
      />

      <RelatorioExtratoDialog
        open={relatorioOpen}
        loading={relatorioMut.isPending}
        onChoose={(imprimeEspelho) => void gerarRelatorio(imprimeEspelho)}
        onClose={() => setRelatorioOpen(false)}
      />

      <ConfirmDialog
        open={reenviarFor !== null}
        title={t('features.empresa.notasFiscais.reenviar.title')}
        description={t('features.empresa.notasFiscais.reenviar.description', {
          numero: reenviarFor?.numero ?? `#${reenviarFor?.id ?? ''}`,
        })}
        confirmLabel={t('features.empresa.notasFiscais.reenviar.confirm')}
        destructive={false}
        loading={reenviarMut.isPending}
        onCancel={() => setReenviarFor(null)}
        onConfirm={async () => {
          if (!reenviarFor) return;
          try {
            await reenviarMut.mutateAsync({
              id: reenviarFor.id,
              empresaId: reenviarFor.empresaId,
            });
            toast.success(t('features.empresa.notasFiscais.toasts.reenviada'));
            setReenviarFor(null);
          } catch (err) {
            toast.error(describeApiError(err));
          }
        }}
      />
    </Stack>
  );
}
